import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef
} from 'react';
import { Source, Layer, Popup } from 'react-map-gl';
import { parseISO, addDays, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import debounce from 'lodash/debounce';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// Maximum number of hours to cache to prevent unlimited memory growth
const MAX_CACHED_HOURS = 6; 

const formatDateUTC = (date) => formatInTimeZone(date, 'UTC', 'yyyy-MM-dd');

function fixDateline(lon1, lon2) {
  const diff = lon2 - lon1;
  if (diff > 180) return lon2 - 360;
  if (diff < -180) return lon2 + 360;
  return lon2;
}

// Cache cleanup helper function
const cleanupCache = (cache, currentDateTime) => {
  const keys = Object.keys(cache);
  if (keys.length <= MAX_CACHED_HOURS) return;
  
  // Get current date and hour for comparison
  const currentHour = currentDateTime.getUTCHours();
  const currentDateStr = formatDateUTC(currentDateTime);
  
  // Score keys by how far they are from current time
  const keyScores = keys.map(key => {
    const [layerId, dateStr, hourStr] = key.split('_');
    const hour = parseInt(hourStr);
    
    // Higher score = further from current time = remove first
    let score = 0;
    if (dateStr !== currentDateStr) {
      score += 100; // Different day, much lower priority
    } else {
      score += Math.abs(hour - currentHour); // Score by hour distance
    }
    
    return { key, score };
  });
  
  // Sort by score (highest first = furthest from current time)
  keyScores.sort((a, b) => b.score - a.score);
  
  // Remove furthest entries until we're back at our limit
  const keysToRemove = keyScores
    .slice(0, keys.length - MAX_CACHED_HOURS)
    .map(item => item.key);
  
  keysToRemove.forEach(key => {
    delete cache[key];
  });
};

const ADSBGlobalLayer = ({
  layerId,
  visible,
  currentDateTime,
  addCachedHour,
  cachedHours,
  isPlaying,
  minAltitude = 25000,
  maxAltitude = 45000,
  selectedFlight,
  onFlightClose,
  currentZoom
}) => {
  const [adsbData, setAdsbData] = useState([]);
  const [filteredTrackData, setFilteredTrackData] = useState([]);
  const [filteredPositionData, setFilteredPositionData] = useState([]);
  const dataCache = useRef({});
  const inProgressCache = useRef(new Set());

  const getColorForAltitude = useCallback((altitude) => {
    const norm = Math.max(
      0,
      Math.min(1, (altitude - minAltitude) / (maxAltitude - minAltitude))
    );
    const red = Math.round(norm * 255);
    const blue = Math.round((1 - norm) * 255);
    return `rgb(${red}, 0, ${blue})`;
  }, [minAltitude, maxAltitude]);

  const getOpacityForTimeDiff = (timeDiff) => {
    // fade line segments older than 10 minutes
    return Math.max(0, Math.min(1, 1 - (Math.abs(timeDiff) / 10)));
  };

  const isDataEmpty = (data) => {
    if (Array.isArray(data)) return data.length === 0;
    if (data && typeof data === 'object') return Object.keys(data).length === 0;
    return !data;
  };

  // Fetch for a single hour, with local caching
  const fetchHourData = useCallback(
    async (layer, dateStr, hour) => {
      const cacheKey = `${layer}_${dateStr}_${hour}`;
      if (dataCache.current[cacheKey] || inProgressCache.current.has(cacheKey)) {
        return dataCache.current[cacheKey] || null;
      }

      inProgressCache.current.add(cacheKey);
      try {
        const resp = await fetch(
          `${API_BASE_URL}/adsb_global?date_str=${dateStr}&hour=${hour}&min_altitude=${minAltitude}`
        );
        if (!resp.ok) {
          const errorText = await resp.text();
          throw new Error(`HTTP error: ${resp.status}, message: ${errorText}`);
        }
        const data = await resp.json();
        if (!isDataEmpty(data)) {
          dataCache.current[cacheKey] = data;
          addCachedHour(
            layer,
            parseISO(`${dateStr}T${String(hour).padStart(2, '0')}:00:00Z`),
            hour
          );
          return data;
        }
        return null;
      } catch (err) {
        console.error(`Error fetching global ADSB data for ${cacheKey}:`, err);
        return null;
      } finally {
        inProgressCache.current.delete(cacheKey);
      }
    },
    [addCachedHour, minAltitude, layerId]
  );

  // This fetches the current hour ±1
  const fetchRelevantData = useCallback(async () => {
    if (!visible) return;

    // Clean up cache before potentially adding more data
    cleanupCache(dataCache.current, currentDateTime);

    const currentHour = currentDateTime.getUTCHours();
    const currentDateStr = formatDateUTC(currentDateTime);
    const prevDateStr = formatDateUTC(subDays(currentDateTime, 1));
    const nextDateStr = formatDateUTC(addDays(currentDateTime, 1));

    const hoursToFetch = [
      { dateStr: currentDateStr, hour: currentHour },
      {
        dateStr: currentHour === 23 ? nextDateStr : currentDateStr,
        hour: (currentHour + 1) % 24
      },
      {
        dateStr: currentHour === 0 ? prevDateStr : currentDateStr,
        hour: (currentHour - 1 + 24) % 24
      }
    ];

    // If all needed hours are in cache, skip fetching
    const allCached = hoursToFetch.every(({ dateStr, hour }) => {
      const cacheKey = `${layerId}_${dateStr}_${hour}`;
      return dataCache.current[cacheKey];
    });
    if (allCached) {
      const allAdsbData = hoursToFetch.flatMap(({ dateStr, hour }) => {
        const key = `${layerId}_${dateStr}_${hour}`;
        return dataCache.current[key] || [];
      });
      setAdsbData(allAdsbData);
      return;
    }

    try {
      // fetch missing hours in parallel
      const fetchedData = await Promise.all(
        hoursToFetch.map(async ({ dateStr, hour }) => {
          const data = await fetchHourData(layerId, dateStr, hour);
          return data || [];
        })
      );
      const allAdsbData = fetchedData.flat();
      setAdsbData(allAdsbData);
    } catch (err) {
      console.error('Error in fetchRelevantData (global):', err);
    }
  }, [visible, currentDateTime, fetchHourData, layerId]);

  // Debounced version of fetchRelevantData (only used when not playing)
  const debouncedFetchRelevantData = useMemo(
    () => debounce(fetchRelevantData, 300),
    [fetchRelevantData]
  );

  useEffect(() => {
    if (isPlaying) {
      // fetch immediately in "play" mode
      fetchRelevantData();
    } else {
      // debounce when paused
      debouncedFetchRelevantData();
    }
    // Clean up the debounced function on unmount
    return () => {
      debouncedFetchRelevantData.cancel();
    };
  }, [isPlaying, currentDateTime, debouncedFetchRelevantData, fetchRelevantData]);

  // Filter data once fetched
  useEffect(() => {
    const currentTimeUnix = currentDateTime.getTime();
    const track = [];
    const positions = [];

    adsbData.forEach((entry) => {
      if (!entry?.time) return;
      const entryTimeUnix = new Date(entry.time).getTime();
      const timeDiffMin = (entryTimeUnix - currentTimeUnix) / (1000 * 60);

      // For track lines: -11 <= diff < 1
      if (timeDiffMin >= -11 && timeDiffMin < 1) track.push(entry);

      // For positions (points): within 1 minute
      if (Math.abs(timeDiffMin) < 1) positions.push(entry);
    });

    track.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    setFilteredTrackData(track);
    setFilteredPositionData(positions);
  }, [adsbData, currentDateTime]);

  // Build line segments for each flight
  const trackFeatures = useMemo(() => {
    const flightPaths = {};
    const now = currentDateTime.getTime();

    filteredTrackData.forEach((entry) => {
      const { hex, lon, lat } = entry || {};
      if (!hex || lon == null || lat == null) return;
      if (!flightPaths[hex]) flightPaths[hex] = [];

      const altitude = entry.alt_baro ?? entry.alt_geom;
      const timeDiff = (new Date(entry.time).getTime() - now) / (1000 * 60);

      flightPaths[hex].push({
        coordinates: [lon, lat],
        altitude,
        timeDiff
      });
    });

    return Object.entries(flightPaths).flatMap(([hex, path]) => {
      const features = [];
      for (let i = 1; i < path.length; i++) {
        const prev = path[i - 1];
        const curr = path[i];
        const [lon1, lat1] = prev.coordinates;
        const [lon2, lat2] = curr.coordinates;
        const fixedLon2 = fixDateline(lon1, lon2);

        features.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [lon1, lat1],
              [fixedLon2, lat2]
            ]
          },
          properties: {
            hex,
            color: getColorForAltitude(curr.altitude),
            opacity: getOpacityForTimeDiff(curr.timeDiff)
          }
        });
      }
      return features;
    });
  }, [filteredTrackData, currentDateTime, getColorForAltitude]);

  // Build circle features for current positions
  const positionFeatures = useMemo(() => {
    return filteredPositionData
      .map((entry) => {
        if (!entry?.lon || !entry?.lat) return null;
        const altitude = entry.alt_baro ?? entry.alt_geom;
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [entry.lon, entry.lat]
          },
          properties: {
            ...entry,
            color: getColorForAltitude(altitude)
          }
        };
      })
      .filter(Boolean);
  }, [filteredPositionData, getColorForAltitude]);

  const trackLayer = useMemo(
    () => ({
      id: `${layerId}-global-adsb-track-layer`,
      type: 'line',
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
        'line-opacity': ['get', 'opacity']
      },
      layout: {
        visibility: visible ? 'visible' : 'none'
      }
    }),
    [layerId, visible]
  );

  const positionLayer = useMemo(
    () => ({
      id: `${layerId}-adsb-global-position-layer`,
      type: 'circle',
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          4, 1,
          8, 4,
          12, 10
        ],
        'circle-color': ['get', 'color'],
        'circle-opacity': 0.7,
        'circle-stroke-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          4, 0.1,
          8, 1,
          12, 1.5
        ],
        'circle-stroke-color': 'white',
        'circle-radius-transition': { duration: 300 },
        'circle-opacity-transition': { duration: 300 }
      },
      layout: {
        visibility: visible ? 'visible' : 'none'
      }
    }),
    [layerId, visible]
  );

  // Clear cache on unmount
  useEffect(() => {
    return () => {
      // Clear all cached data on unmount to free memory
      dataCache.current = {};
      inProgressCache.current.clear();
    };
  }, []);

  const getDisplayName = (key) => {
    const displayNames = {
      flight: 'Flight',
      fid: 'Flight ID',
      hex: 'ICAO Address',
      alt_baro: 'Barometric Altitude',
      alt_geom: 'Geometric Altitude',
      lat: 'Latitude',
      lon: 'Longitude',
      r: 'Registration',
      t: 'Aircraft Type',
      al: 'Airline IATA code',
      time: 'Timestamp',
      cs: 'Callsign',
      dep: 'Departure',
      arr: 'Arrival',
      dep_t: 'Scheduled Departure Time',
      arr_t: 'Scheduled Arrival Time',
      ct: 'Collection Type'
    };
    return displayNames[key] || key;
  };

  const formatPropertyValue = (key, value) => {
    switch (key) {
      case 'alt_baro':
      case 'alt_geom':
        return `${Math.round(value)} ft`;
      case 'lat':
      case 'lon':
        return `${Math.round(value * 10000) / 10000}°`;
      default:
        return value;
    }
  };

  const propertyCategories = {
    'Flight Info': ['flight', 'fid', 'hex', 'al', 'cs', 'r', 't', 'ct'],
    Position: ['lat', 'lon', 'alt_baro', 'alt_geom'],
    'Departure/Arrival': ['dep', 'dep_t', 'arr', 'arr_t'],
    Timestamp: ['time']
  };

  const renderPopupContent = () => {
    if (!selectedFlight) return null;
    return (
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <h3>{selectedFlight.flight || selectedFlight.hex || selectedFlight.cs}</h3>
        {Object.entries(propertyCategories).map(([category, props]) => (
          <div key={category}>
            <h4>{category}</h4>
            {props.map((key) => {
              if (key in selectedFlight && selectedFlight[key] !== null) {
                return (
                  <p key={key} style={{ margin: '5px 0' }}>
                    <strong>{getDisplayName(key)}:</strong>{' '}
                    {formatPropertyValue(key, selectedFlight[key])}
                  </p>
                );
              }
              return null;
            })}
          </div>
        ))}
      </div>
    );
  };

  if (!visible) return null;

  return (
    <>
      <Source
        id={`${layerId}-adsb-global-tracks`}
        type="geojson"
        data={{ type: 'FeatureCollection', features: trackFeatures }}
      >
        <Layer {...trackLayer} />
      </Source>
      <Source
        id={`${layerId}-adsb-global-positions`}
        type="geojson"
        data={{ type: 'FeatureCollection', features: positionFeatures }}
      >
        <Layer {...positionLayer} />
      </Source>

      {selectedFlight?.lon && selectedFlight?.lat && (
        <Popup
          longitude={parseFloat(selectedFlight.lon)}
          latitude={parseFloat(selectedFlight.lat)}
          onClose={onFlightClose}
          closeButton={true}
          closeOnClick={false}
          maxWidth="300px"
        >
          {renderPopupContent()}
        </Popup>
      )}
    </>
  );
};

export default ADSBGlobalLayer;